import React, { useState, useEffect } from "react";
import { countries } from "../../store/countries";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-modal";
import { PieChart, Pie, Cell } from "recharts";
import { FaArrowLeftLong } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";

const SERVER_URL = "https://server.1.strategytracker.net";

// Thematic areas configuration
const THEMATIC_AREAS = {
  Health: {
    id: 4,
    label: "Health",
    sections: [
      { id: "key-findings", label: "Key Findings", subCategory: "Community" },
      {
        id: "health-facilities",
        label: "Health Facilities",
        subCategory: "Health Officials",
      },
      {
        id: "eye-cataract",
        label: "Eye-Cataract",
        subCategory: "Medical Camp Beneficiary",
      },
      { id: "inclusivity", label: "Inclusivity", subCategory: "Community" },
    ],
  },
  Education: {
    id: 1,
    label: "Education",
    sections: [
      { id: "key-findings", label: "Key Findings", subCategory: "Principal" },
      { id: "enrolment", label: "Enrolment", subCategory: "Principal" },
      { id: "facilities", label: "Facilities", subCategory: "Principal" },
      {
        id: "academic-output",
        label: "Academic Output",
        subCategory: "Teacher",
      },
      {
        id: "teachers-skills",
        label: "Teachers Skills",
        subCategory: "Teacher",
      },
      {
        id: "online-learning",
        label: "Online Learning IT",
        subCategory: "Teacher",
      },
      {
        id: "career-counseling",
        label: "Career Counselling & Life Skills",
        subCategory: "Teacher",
      },
      {
        id: "policies-systems",
        label: "Policies & Systems",
        subCategory: "Principal",
      },
      {
        id: "financial-support",
        label: "Financial Support",
        subCategory: "Parent Guardian",
      },
    ],
  },
  WASH: {
    id: 6,
    label: "WASH",
    sections: [
      {
        id: "key-findings",
        label: "Key Findings",
        subCategory: "WASH Committee",
      },
      {
        id: "sustainability",
        label: "Sustainability",
        subCategory: "Community",
      },
      {
        id: "community-ownership",
        label: "Community Ownership",
        subCategory: "Community",
      },
      { id: "water-quality", label: "Water Quality", subCategory: "Community" },
      { id: "sanitation", label: "Sanitation", subCategory: "Community" },
      { id: "hygiene", label: "Hygiene", subCategory: "Community" },
      {
        id: "accessibility-standards",
        label: "Accessibility & Standards",
        subCategory: "WASH Official",
      },
    ],
  },
  "Food Security": {
    id: 3,
    label: "Food Security",
    sections: [
      { id: "key-findings", label: "Key Findings", subCategory: "Student" },
      { id: "scope", label: "Scope", subCategory: "Food Distribution" },
    ],
  },
  Emergencies: {
    id: 2,
    label: "Emergencies",
    sections: [
      {
        id: "key-findings",
        label: "Key Findings",
        subCategory: "EWS member v5",
      },
      {
        id: "disaster-reduction",
        label: "Disaster Reduction",
        subCategory: "Community of Disaster hit areas",
      },
      {
        id: "assistance",
        label: "Assistance",
        subCategory: "Community of Disaster hit areas",
      },
    ],
  },
  Livelihoods: {
    id: 5,
    label: "Livelihoods",
    sections: [
      { id: "key-findings", label: "Key Findings", subCategory: "Farmer" },
      {
        id: "farming-practices",
        label: "Farming Practices",
        subCategory: "Farmer",
      },
      { id: "facilities", label: "Facilities", subCategory: "Farmer" },
      { id: "trainings", label: "Trainings", subCategory: "Trainee" },
    ],
  },
};

// Response type options
const RESPONSE_TYPES = [
  { value: "yes_no", label: "Yes/No", options: ["Yes", "No"] },
  {
    value: "satisfied",
    label: "Satisfied/Not Satisfied/Neutral",
    options: ["Satisfied", "Not Satisfied", "Neutral"],
  },
  {
    value: "yes_no_neutral",
    label: "Yes/No/Neutral",
    options: ["Yes", "No", "Neutral"],
  },
  {
    value: "yes_no_neutral_none",
    label: "Yes/No/Neutral/None",
    options: ["Yes", "No", "Neutral", "None"],
  },
];

const ManagePage = () => {
  const [filters, setFilters] = useState({
    country: "",
    countryName: "",
    thematicArea: "",
    section: "",
    year: "",
  });
  const navigate = useNavigate();
  const [loadingQuestionResponse, setLoadingQuestionResponse] = useState({});
  const [jsonModalIsOpen, setJsonModalIsOpen] = useState(false);
  const [jsonDataToDisplay, setJsonDataToDisplay] = useState("");
  const [rows, setRows] = useState([
    {
      questionId: "",
      questionText: "",
      narrative: "",
      responseType: "",
      targetResponses: [],
      placement: "",
      section: "",
      endpoint: "",
      availableResponseLabels: [], // Store the actual labels from API
    },
  ]);

  // Get available sections based on selected thematic area
  const availableSections = filters.thematicArea
    ? THEMATIC_AREAS[filters.thematicArea]?.sections || []
    : [];

  // Helper function to detect response type from API labels
  const detectResponseType = (labels) => {
    if (!labels || labels.length === 0) return null;

    const labelsLower = labels.map((l) => String(l).toLowerCase().trim());
    const labelsSet = new Set(labelsLower);

    // Check for Yes/No/Neutral/None pattern (4 options)
    if (
      labelsSet.has("yes") &&
      labelsSet.has("no") &&
      labelsSet.has("neutral") &&
      labelsSet.has("none") &&
      labels.length === 4
    ) {
      return "yes_no_neutral_none";
    }

    // Check for Yes/No/Neutral pattern (3 options)
    if (
      labelsSet.has("yes") &&
      labelsSet.has("no") &&
      labelsSet.has("neutral") &&
      labels.length === 3
    ) {
      return "yes_no_neutral";
    }

    // Check for Satisfied/Not Satisfied/Neutral pattern (3 options)
    const hasSatisfied = labelsLower.some(
      (l) => l.includes("satisfied") || l.includes("satisfaction"),
    );
    const hasNotSatisfied = labelsLower.some(
      (l) =>
        l.includes("not satisfied") ||
        l.includes("notsatisfied") ||
        l.includes("unsatisfied") ||
        l.includes("not satisfied"),
    );
    if (
      hasSatisfied &&
      hasNotSatisfied &&
      labelsSet.has("neutral") &&
      labels.length === 3
    ) {
      return "satisfied";
    }

    // Check for Yes/No pattern (2 options)
    if (labelsSet.has("yes") && labelsSet.has("no") && labels.length === 2) {
      return "yes_no";
    }

    // Default: return null and let user select
    return null;
  };

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = { ...prev, [name]: value };
      if (name === "country") {
        const selectedCountry = countries.find(
          (c) => String(c.id) === String(value),
        );
        newFilters.countryName = selectedCountry?.name || "";
      }
      // Reset section when thematic area changes
      if (name === "thematicArea") {
        newFilters.section = "";
      }
      return newFilters;
    });
  };

  // Reset filters
  const handleReset = () => {
    setFilters({
      country: "",
      countryName: "",
      thematicArea: "",
      section: "",
      year: "",
    });
    setRows([
      {
        questionId: "",
        questionText: "",
        narrative: "",
        responseType: "",
        targetResponses: [],
        placement: "",
        section: "",
        endpoint: "",
        availableResponseLabels: [],
      },
    ]);
    setLoadingQuestionResponse({});
    toast.info("Filters have been reset.");
  };

  // Handle fetching question by ID
  const handleFetchQuestion = async (rowIndex) => {
    const questionId = rows[rowIndex].questionId;

    if (!questionId || !questionId.trim()) {
      toast.error("Please enter a question ID.");
      return;
    }

    const endpoint = `${SERVER_URL}/api/data/standard/question/${questionId.trim()}`;
    setLoadingQuestionResponse((prev) => ({ ...prev, [rowIndex]: true }));

    try {
      // Fetch the question's response data from endpoint
      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiData = await response.json();

      // Extract question text from API response
      const questionText = apiData?.data?.question?.text || "";

      // Extract labels from API response
      const labels = apiData?.data?.labels || [];

      if (labels.length === 0) {
        toast.warning("No response labels found in the API response.");
      }

      // Map labels to target responses
      const targetResponses = labels.map((label, idx) => ({
        text: label,
        id: `response_${idx}`,
      }));

      // Use "dynamic" as response type when we have labels from API
      const responseTypeToUse = labels.length > 0 ? "dynamic" : "";

      // Update the row with question data and dynamic response type
      setRows((prevRows) => {
        const newRows = [...prevRows];
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          questionText: questionText,
          narrative: questionText, // Pre-fill narrative with question text
          responseType: responseTypeToUse,
          targetResponses: targetResponses,
          availableResponseLabels: labels, // Store labels for dynamic response type
          endpoint: endpoint,
        };
        return newRows;
      });

      if (labels.length > 0) {
        toast.success(
          `Question loaded with ${labels.length} response option(s): ${labels.join(", ")}`,
        );
      } else {
        toast.success("Question loaded. No response options found.");
      }
    } catch (error) {
      console.error("Error fetching question response:", error);
      toast.error(
        `Failed to fetch question data. ${error.message || "Please check the question ID."}`,
      );

      // Clear question data on error
      setRows((prevRows) => {
        const newRows = [...prevRows];
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          questionText: "",
          narrative: "",
          responseType: "",
          targetResponses: [],
          availableResponseLabels: [],
          endpoint: "",
        };
        return newRows;
      });
    } finally {
      setLoadingQuestionResponse((prev) => ({ ...prev, [rowIndex]: false }));
    }
  };

  // Handle response type change
  const handleResponseTypeChange = (e, rowIndex) => {
    const responseType = e.target.value;
    const row = rows[rowIndex];

    // If we have available labels from API and user selects "dynamic", use those
    if (
      responseType === "dynamic" &&
      row.availableResponseLabels &&
      row.availableResponseLabels.length > 0
    ) {
      // Use the actual labels from API
      setRows((prevRows) => {
        const newRows = [...prevRows];
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          responseType: responseType,
          targetResponses: row.availableResponseLabels.map((label, idx) => ({
            text: label,
            id: `response_${idx}`,
          })),
        };
        return newRows;
      });
    } else if (
      row.availableResponseLabels &&
      row.availableResponseLabels.length > 0
    ) {
      // If user selects a predefined type but we have API labels, still use API labels
      setRows((prevRows) => {
        const newRows = [...prevRows];
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          responseType: responseType,
          targetResponses: row.availableResponseLabels.map((label, idx) => ({
            text: label,
            id: `response_${idx}`,
          })),
        };
        return newRows;
      });
    } else {
      // Fallback to predefined response types when no API labels available
      const responseTypeConfig = RESPONSE_TYPES.find(
        (rt) => rt.value === responseType,
      );
      setRows((prevRows) => {
        const newRows = [...prevRows];
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          responseType: responseType,
          targetResponses: responseTypeConfig
            ? responseTypeConfig.options.map((opt, idx) => ({
                text: opt,
                id: `response_${idx}`,
              }))
            : [],
        };
        return newRows;
      });
    }
  };

  // Handle response removal
  const handleRemoveResponse = (rowIndex, responseIndex) => {
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex].targetResponses.splice(responseIndex, 1);
      return newRows;
    });
  };

  // Handle row changes
  const handleRowChange = (e, rowIndex) => {
    const { name, value } = e.target;
    setRows((prevRows) => {
      const newRows = [...prevRows];
      newRows[rowIndex][name] = value;
      return newRows;
    });
  };

  // Add a new row
  const handleAddRow = () => {
    setRows((prevRows) => [
      ...prevRows,
      {
        questionId: "",
        questionText: "",
        narrative: "",
        responseType: "",
        targetResponses: [],
        placement: "",
        section: filters.section || "",
        endpoint: "",
        availableResponseLabels: [],
      },
    ]);
  };

  // Remove a row
  const handleRemoveRow = (rowIndex) => {
    setRows((prevRows) => prevRows.filter((_, index) => index !== rowIndex));
  };

  // Save the data
  const handleSave = () => {
    if (!filters.thematicArea || !filters.section || !filters.year) {
      toast.error("Please select all required filters.");
      return;
    }

    const thematicAreaConfig = THEMATIC_AREAS[filters.thematicArea];
    const sectionConfig = availableSections.find(
      (s) => s.id === filters.section,
    );

    // Filter rows that have both questionId and narrative
    const validMappings = rows.filter((row) => row.questionId && row.narrative);

    if (validMappings.length === 0) {
      toast.error(
        "Please add at least one mapping with a question ID and narrative.",
      );
      return;
    }

    const jsonData = {
      country: filters.country ? String(filters.country) : null,
      country_id: filters.country ? String(filters.country) : null,
      country_name: filters.countryName || "",
      category: thematicAreaConfig.label,
      sub_category: sectionConfig.subCategory,
      section: filters.section,
      year: String(filters.year),
      mappings: validMappings.map((row) => ({
        endpoint:
          row.endpoint ||
          `${SERVER_URL}/api/data/standard/question/${row.questionId}`,
        narrative: row.narrative,
        placement: row.placement ? parseInt(row.placement) : null,
        questionId: String(row.questionId),
        questionText: row.questionText || "",
        responseType: row.responseType || "dynamic",
        section: row.section || filters.section,
        targetResponses: row.targetResponses.map((response) => ({
          id: response.id,
          text: response.text,
        })),
      })),
    };

    fetch(`${SERVER_URL}/api/mapping/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(jsonData),
    })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((err) => {
            throw new Error(
              err.message || `HTTP error! status: ${response.status}`,
            );
          });
        }
        return response.json();
      })
      .then((data) => {
        if (data.success) {
          toast.success(data.message || "Mapping created successfully.");
        } else {
          toast.error(data.message || "Failed to create mapping.");
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        toast.error(error.message || "Error saving data. Please try again.");
      });
  };

  // Function to log JSON and display in modal
  const logJson = () => {
    const thematicAreaConfig = THEMATIC_AREAS[filters.thematicArea];
    const sectionConfig = availableSections.find(
      (s) => s.id === filters.section,
    );

    const jsonData = {
      country: filters.country || null,
      country_id: filters.country || null,
      country_name: filters.countryName || "",
      category: thematicAreaConfig?.label || "",
      sub_category: sectionConfig?.subCategory || "",
      section: filters.section || "",
      year: filters.year || "",
      mappings: rows
        .filter((row) => row.questionId && row.narrative)
        .map((row) => ({
          questionId: row.questionId,
          questionText: row.questionText,
          narrative: row.narrative,
          responseType: row.responseType,
          targetResponses: row.targetResponses.map((response) => ({
            text: response.text,
            id: response.id,
          })),
          placement: row.placement ? parseInt(row.placement) : null,
          section: row.section || filters.section,
          endpoint: row.endpoint,
        })),
    };

    const formattedJson = JSON.stringify(jsonData, null, 2);

    // Display in modal
    setJsonDataToDisplay(formattedJson);
    setJsonModalIsOpen(true);

    toast.success("JSON data displayed. Check the modal and browser console.");
  };

  // Copy JSON to clipboard
  const copyJsonToClipboard = () => {
    navigator.clipboard
      .writeText(jsonDataToDisplay)
      .then(() => {
        toast.success("JSON data copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy:", err);
        toast.error("Failed to copy to clipboard.");
      });
  };

  // Sample data for the pie chart
  const data = [
    { name: "Group A", value: 400 },
    { name: "Group B", value: 300 },
    { name: "Group C", value: 300 },
    { name: "Group D", value: 200 },
  ];

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  const getPlacementStyle = (placement) => {
    if (!placement) return {};
    const row = Math.floor((placement - 1) / 5) + 1;
    const col = ((placement - 1) % 5) + 1;
    return { gridRow: row, gridColumn: col };
  };

  return (
    <div className="container mx-auto">
      <div className="bg-white rounded wrapper">
        <div className="flex items-center gap-3">
          <FaArrowLeftLong
            className="cursor-pointer"
            onClick={() => {
              navigate("/qualitative-assessment-dashboard");
            }}
          />
          <h3 className="text-lg font-semibold">
            Manage Qualitative Assessment Mappings
          </h3>
        </div>
        <div className="mt-3 mb-4 border-b" />

        {/* Filters Section */}
        <div className="flex flex-col w-full p-5 mb-6 bg-white border border-gray-200 rounded-lg">
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
              {/* Country Filter */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-neutral-600">
                  Select Country
                </label>
                <select
                  name="country"
                  value={filters.country}
                  onChange={handleFilterChange}
                  className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                >
                  <option value="">Select Country (Optional)</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Thematic Area Filter */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-neutral-600">
                  Select Thematic Area <span className="text-red-500">*</span>
                </label>
                <select
                  name="thematicArea"
                  value={filters.thematicArea}
                  onChange={handleFilterChange}
                  className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                >
                  <option value="">Select Thematic Area</option>
                  {Object.keys(THEMATIC_AREAS).map((area) => (
                    <option key={area} value={area}>
                      {THEMATIC_AREAS[area].label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Section Filter */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-neutral-600">
                  Select Section <span className="text-red-500">*</span>
                </label>
                <select
                  name="section"
                  value={filters.section}
                  onChange={handleFilterChange}
                  disabled={!filters.thematicArea}
                  className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select Section</option>
                  {availableSections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Filter */}
              <div className="space-y-1">
                <label className="block text-sm font-medium text-neutral-600">
                  Select Year <span className="text-red-500">*</span>
                </label>
                <select
                  name="year"
                  value={filters.year}
                  onChange={handleFilterChange}
                  className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                >
                  <option value="">Select Year</option>
                  {[ 2025, 2026].map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Reset Button */}
            <div className="flex justify-end">
              <button
                onClick={handleReset}
                className="px-5 py-2.5 text-white bg-gray-400 hover:bg-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-300"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Mapping Rows */}
        {filters.thematicArea && filters.section && filters.year && (
          <>
            {rows.map((row, rowIndex) => (
              <div
                key={rowIndex}
                className="flex flex-col w-full p-5 mb-6 bg-white border border-gray-200 rounded-lg"
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {/* Question ID Input */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-neutral-600">
                        Question ID <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          name="questionId"
                          value={row.questionId}
                          onChange={(e) => handleRowChange(e, rowIndex)}
                          placeholder="Enter question ID (e.g., 2132)"
                          className="flex-1 block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                        />
                        <button
                          onClick={() => handleFetchQuestion(rowIndex)}
                          disabled={
                            loadingQuestionResponse[rowIndex] || !row.questionId
                          }
                          className="px-4 py-2 text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loadingQuestionResponse[rowIndex]
                            ? "Loading..."
                            : "Select"}
                        </button>
                      </div>
                      {row.endpoint && (
                        <div className="mt-1 text-xs text-gray-500">
                          Endpoint: {row.endpoint}
                        </div>
                      )}
                    </div>

                    {/* Response Type */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-neutral-600">
                        Response Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="responseType"
                        value={row.responseType}
                        onChange={(e) => handleResponseTypeChange(e, rowIndex)}
                        disabled={
                          !row.availableResponseLabels ||
                          row.availableResponseLabels.length === 0
                        }
                        className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {row.availableResponseLabels &&
                          row.availableResponseLabels.length > 0
                            ? "Select Response Type"
                            : "Fetch question first"}
                        </option>
                        {row.availableResponseLabels &&
                        row.availableResponseLabels.length > 0 ? (
                          // Show dynamic response type based on actual labels from API
                          <option value="dynamic">
                            Dynamic: {row.availableResponseLabels.join(" / ")}
                          </option>
                        ) : (
                          // Fallback to predefined types if no labels available
                          RESPONSE_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))
                        )}
                      </select>
                      {row.availableResponseLabels &&
                        row.availableResponseLabels.length > 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            Available responses:{" "}
                            {row.availableResponseLabels.join(", ")}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Question Text Display */}
                  {row.questionText && (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-neutral-600">
                        Question Text
                      </label>
                      <div className="block w-full px-5 py-3 text-base text-gray-700 bg-gray-100 border border-gray-200 rounded-lg">
                        {row.questionText}
                      </div>
                    </div>
                  )}

                  {/* Narrative Field */}
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-neutral-600">
                      Key Finding Narrative{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="narrative"
                      value={row.narrative}
                      onChange={(e) => handleRowChange(e, rowIndex)}
                      placeholder="Enter the key finding text to display (e.g., 'Community reported that MH BHUs have the medicines available for patients')"
                      rows={3}
                      className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                    />
                  </div>

                  {/* Target Responses Display */}
                  {row.responseType && row.targetResponses.length > 0 && (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-neutral-600">
                        Target Responses
                      </label>
                      <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-gray-50">
                        {row.targetResponses.map((response, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 text-sm font-semibold text-gray-700 bg-blue-200 rounded-full"
                          >
                            {response.text}
                            <button
                              onClick={() =>
                                handleRemoveResponse(rowIndex, index)
                              }
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Placement Field */}
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-neutral-600">
                        Placement (Position in grid)
                      </label>
                      <input
                        type="number"
                        name="placement"
                        value={row.placement}
                        onChange={(e) => handleRowChange(e, rowIndex)}
                        placeholder="Enter position number (1-25)"
                        min="1"
                        max="25"
                        className="block w-full px-5 py-3 text-base placeholder-gray-300 transition duration-500 ease-in-out transform border border-transparent rounded-lg text-neutral-600 bg-gray-50 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-300"
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleRemoveRow(rowIndex)}
                      className="px-5 py-2.5 text-white bg-red-500 hover:bg-red-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-300"
                    >
                      Delete Row
                    </button>
                    {rowIndex === rows.length - 1 && (
                      <button
                        onClick={handleAddRow}
                        className="px-5 py-2.5 text-white bg-green-500 hover:bg-green-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-300"
                      >
                        Add Row
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}

        {/* Save and Log Buttons */}
        {filters.thematicArea && filters.section && filters.year && (
          <div className="flex justify-end mb-6 space-x-2">
            <button
              onClick={logJson}
              className="px-5 py-2.5 text-white bg-gray-500 hover:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
            >
              Log JSON
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2.5 text-white bg-blue-500 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-300"
            >
              Save Mappings
            </button>
          </div>
        )}

        {/* Preview Section */}
        {rows.some((row) => row.placement) && (
          <div className="mt-8">
            <h2 className="mb-4 text-lg font-semibold">Preview</h2>
            <div className="grid grid-cols-5 gap-5">
              {rows
                .filter((row) => row.placement)
                .sort((a, b) => parseInt(a.placement) - parseInt(b.placement))
                .map((row, rowIndex) => (
                  <div
                    key={rowIndex}
                    className="flex flex-col items-center p-4 bg-gray-100 border border-gray-300 rounded-lg"
                    style={getPlacementStyle(row.placement)}
                  >
                    <PieChart width={100} height={100}>
                      <Pie
                        data={data}
                        cx={50}
                        cy={50}
                        innerRadius={30}
                        outerRadius={40}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {data.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                    <p className="mt-2 text-sm font-medium text-center text-gray-700">
                      {row.narrative || "No narrative"}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* JSON Display Modal */}
        <Modal
          isOpen={jsonModalIsOpen}
          onRequestClose={() => setJsonModalIsOpen(false)}
          contentLabel="JSON Data"
          className="modal"
          overlayClassName="modal-overlay"
        >
          <div className="max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">JSON Data</h2>
              <div className="flex gap-2">
                <button
                  onClick={copyJsonToClipboard}
                  className="px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:bg-green-600"
                >
                  Copy to Clipboard
                </button>
                <button
                  onClick={() => setJsonModalIsOpen(false)}
                  className="px-4 py-2 text-sm text-white bg-gray-500 rounded-lg hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
            <div className="p-4 overflow-x-auto font-mono text-sm text-green-400 bg-gray-900 rounded-lg">
              <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {jsonDataToDisplay || "No data to display"}
              </pre>
            </div>
            <div className="mt-4 text-sm text-gray-600">
              <p>
                💡 Tip: This data is also logged to the browser console. Press
                F12 to open Developer Tools.
              </p>
            </div>
          </div>
        </Modal>

        <style jsx>{`
          .modal {
            position: absolute;
            top: 50%;
            left: 50%;
            right: auto;
            bottom: auto;
            margin-right: -50%;
            transform: translate(-50%, -50%);
            background: white;
            padding: 20px;
            border-radius: 8px;
            max-width: 900px;
            width: 90%;
            max-height: 90vh;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            z-index: 1001;
          }
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            z-index: 1000;
          }
        `}</style>
      </div>
    </div>
  );
};

export default ManagePage;
